import { Maskito, maskitoTransform, maskitoUpdateElement } from "@maskito/core";
import type {
  MaskitoOptions,
  MaskitoPlugin,
  MaskitoPostprocessor,
  MaskitoPreprocessor,
} from "@maskito/core";

/**
 * Маска даты на maskito, которая повторяет поведение прежней маски полей дат (до JS-3094):
 * поле всегда держит шаблон целиком, ввод перезаписывает символы шаблона,
 * стирание возвращает на место символ шаблона, а не сдвигает остальные цифры.
 */
export interface DateMaskConfig {
  /** Маска: `9` — цифра, остальное — постоянные символы, например `99.99.9999` */
  mask: string;
  /** Шаблон той же длины, что и маска, например `dd.mm.yyyy` */
  placeholder: string;
  /** Показывать шаблон и без фокуса (как `alwaysShowMask`) */
  alwaysShowMask?: boolean;
}

const EDITABLE_CHAR = "9";

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const isEditablePosition = (mask: string, position: number) =>
  mask[position] === EDITABLE_CHAR;

const isDigit = (char: string | undefined) => !!char && /\d/.test(char);

const getLeftEditablePosition = (mask: string, position: number) => {
  for (let i = position; i >= 0; i--) {
    if (isEditablePosition(mask, i)) {
      return i;
    }
  }

  return null;
};

const getRightEditablePosition = (mask: string, position: number) => {
  for (let i = position; i < mask.length; i++) {
    if (isEditablePosition(mask, i)) {
      return i;
    }
  }

  return null;
};

// На месте цифры допускаем и символ шаблона: значение в поле всегда полной длины
const createMaskExpression = ({ mask, placeholder }: DateMaskConfig) =>
  Array.from(mask).map((char, i) =>
    char === EDITABLE_CHAR
      ? new RegExp(`[\\d${escapeRegExp(placeholder[i])}]`)
      : char
  );

const fillWithPlaceholder = (value: string, placeholder: string) =>
  value + placeholder.slice(value.length);

// Символы шаблона в выделенном диапазоне, постоянные символы маски не трогаем
const clearRange = (
  value: string,
  { mask, placeholder }: DateMaskConfig,
  from: number,
  to: number
) =>
  Array.from(value)
    .map((char, i) =>
      i >= from && i < to && isEditablePosition(mask, i) ? placeholder[i] : char
    )
    .join("");

const getFilledLength = (value: string, mask: string) => {
  for (let i = value.length - 1; i >= 0; i--) {
    if (isEditablePosition(mask, i) && isDigit(value[i])) {
      return i + 1;
    }
  }

  return 0;
};

const isValueFilled = (value: string, mask: string) =>
  Array.from(mask).every(
    (char, i) => char !== EDITABLE_CHAR || isDigit(value[i])
  );

const isValueEmpty = (value: string, mask: string) =>
  Array.from(value).every(
    (char, i) => !isEditablePosition(mask, i) || !isDigit(char)
  );

// Каретка на первой незаполненной позиции
const getDefaultCaret = (value: string, mask: string) =>
  getRightEditablePosition(mask, getFilledLength(value, mask)) ?? mask.length;

/**
 * Значение для отображения: введённые цифры на своих местах, остальное — шаблон.
 * Строка в другом формате (например `dd/MM/yyyy` для маски `99.99.9999`)
 * раскладывается по маске: цифры встают на свои позиции, чужие разделители отбрасываются.
 */
export const formatDateMaskValue = (value: string, config: DateMaskConfig) => {
  if (!value) {
    return config.placeholder;
  }

  return fillWithPlaceholder(
    maskitoTransform(value, { mask: createMaskExpression(config) }),
    config.placeholder
  );
};

export const createDateMaskOptions = (
  config: DateMaskConfig
): MaskitoOptions => {
  const { mask, placeholder, alwaysShowMask = false } = config;

  // Без выделения Backspace/Delete стирают ближайшую цифру, перескакивая через разделитель.
  // Maskito передаёт в препроцессор уже расширенное выделение, поэтому запоминаем исходное.
  let isCaretCollapsed = true;

  const selectionTrackerPlugin: MaskitoPlugin = (element) => {
    const handleBeforeInput = () => {
      isCaretCollapsed = element.selectionStart === element.selectionEnd;
    };

    element.addEventListener("beforeinput", handleBeforeInput);

    return () => element.removeEventListener("beforeinput", handleBeforeInput);
  };

  // При фокусе показываем шаблон и ставим каретку на первую пустую позицию.
  // Браузер выставляет каретку после события focus, поэтому повторяем на следующем кадре.
  const focusPlugin: MaskitoPlugin = (element) => {
    const handleFocus = () => {
      if (isValueFilled(element.value, mask)) {
        return;
      }

      const value = formatDateMaskValue(element.value, config);
      const caret = getDefaultCaret(value, mask);

      maskitoUpdateElement(element, { value, selection: [caret, caret] });

      requestAnimationFrame(() => {
        if (!element.matches(":focus") || element.value !== value) {
          return;
        }

        element.setSelectionRange(caret, caret);
      });
    };

    element.addEventListener("focus", handleFocus);

    return () => element.removeEventListener("focus", handleFocus);
  };

  // Короткий клик по незаполненному полю тоже ставит каретку на первую пустую позицию
  const clickPlugin: MaskitoPlugin = (element) => {
    const handleMouseDown = (event: MouseEvent) => {
      if (element.matches(":focus") || isValueFilled(element.value, mask)) {
        return;
      }

      const { clientX, clientY } = event;
      const mouseDownTime = Date.now();

      const handleMouseUp = (mouseUpEvent: MouseEvent) => {
        if (!element.matches(":focus")) {
          return;
        }

        const axisDelta = Math.max(
          Math.abs(mouseUpEvent.clientX - clientX),
          Math.abs(mouseUpEvent.clientY - clientY)
        );
        const timeDelta = Date.now() - mouseDownTime;
        const isClick =
          (axisDelta <= 10 && timeDelta <= 200) ||
          (axisDelta <= 5 && timeDelta <= 300);

        if (!isClick) {
          return;
        }

        const caret = getDefaultCaret(element.value, mask);

        element.setSelectionRange(caret, caret);
      };

      element.ownerDocument.addEventListener("mouseup", handleMouseUp, {
        once: true,
      });
    };

    element.addEventListener("mousedown", handleMouseDown);

    return () => element.removeEventListener("mousedown", handleMouseDown);
  };

  // Без alwaysShowMask пустой шаблон убираем при потере фокуса, и снова виден placeholder поля
  const blurPlugin: MaskitoPlugin = (element) => {
    const handleBlur = () => {
      if (!isValueEmpty(element.value, mask)) {
        return;
      }

      maskitoUpdateElement(element, "");
    };

    element.addEventListener("blur", handleBlur);

    return () => element.removeEventListener("blur", handleBlur);
  };

  const preprocessor: MaskitoPreprocessor = (
    { elementState, data },
    actionType
  ) => {
    const { value, selection } = elementState;
    const [from, to] = selection;

    if (actionType === "insert") {
      // Как и прежний beforeMaskedStateChange: вставку не-числа отклоняем целиком.
      // Пустые данные при свёрнутом выделении maskito считает невалидным вводом и отменяет событие.
      const digits = data.replace(/\D/g, "");

      if (Number.isNaN(Number(data)) || !digits) {
        return {
          elementState: { value, selection: [from, from] },
          data: "",
        };
      }

      // Ввод поверх выделения сначала очищает его, затем цифры перезаписывают шаблон с начала выделения
      return {
        elementState: {
          value: clearRange(value, config, from, to),
          selection: [from, from],
        },
        data: digits,
      };
    }

    if (actionType === "deleteBackward" || actionType === "deleteForward") {
      if (isCaretCollapsed && to - from === 1) {
        const position =
          actionType === "deleteBackward"
            ? getLeftEditablePosition(mask, from)
            : getRightEditablePosition(mask, from);

        if (position === null) {
          return { elementState: { value, selection: [from, from] } };
        }

        return {
          elementState: {
            value: clearRange(value, config, position, position + 1),
            selection: [position, position],
          },
        };
      }

      return {
        elementState: {
          value: clearRange(value, config, from, to),
          selection: [from, from],
        },
      };
    }

    return { elementState, data };
  };

  // Непустое значение всегда дополняем шаблоном до полной длины
  const postprocessor: MaskitoPostprocessor = ({ value, selection }) => ({
    value: value ? fillWithPlaceholder(value, placeholder) : value,
    selection,
  });

  return {
    mask: createMaskExpression(config),
    overwriteMode: "replace",
    preprocessors: [preprocessor],
    postprocessors: [postprocessor],
    plugins: [
      selectionTrackerPlugin,
      focusPlugin,
      clickPlugin,
      ...(alwaysShowMask ? [] : [blurPlugin]),
    ],
  };
};

/**
 * Подключает маску даты к элементу input, возвращает функцию отключения.
 * Изменения значения приходят событием input, поэтому в поле нужен обработчик onInput, а не onChange:
 * maskito меняет value программно, и React на такие изменения onChange не вызывает.
 */
export const attachDateMask = (
  element: HTMLInputElement,
  config: DateMaskConfig
) => {
  const maskito = new Maskito(element, createDateMaskOptions(config));

  return () => maskito.destroy();
};
